/*
 * The model's reasoning in attorney language: the harness plumbing it thinks
 * aloud about (tool calls, ids, workspace paths, reminder tags, flags) is
 * dropped, and a folded row reads one short sentence of what it was about.
 */

const TAG = /<\/?[a-z][\w-]*(?:\s[^<>]*)?>/gi;
const REMINDER = /\b(?:system|context)[-_ ]reminders?\b/gi;
const CODE_SPAN = /`[^`\n]*`/g;
/** `load_tools(query="split")`, `open_case()`: a snake_case call. */
const TOOL_CALL = /\b[a-z]+(?:_[a-z0-9]+)+\s*\([^()\n]*\)/g;
/** A bare tool name: `load_tools`, `get_uscis_status`. */
const SNAKE_NAME = /\b[a-z]+(?:_[a-z0-9]+){1,}\b/g;
const UUID =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
/** Firestore-style ids: 16+ letters and digits with both in them. */
const OPAQUE_ID =
  /\b(?=[A-Za-z0-9]*\d)(?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]{16,}\b/g;
/** `draft:abc123`, `node:n-rfe@p2`. */
const KIND_ID =
  /\b(?:draft|node|file|form|packet|plan|thread|run|case|fn|ind):[\w@.-]+/gi;
const PATH =
  /(?:\b[\w.-]+\/)+[\w.-]+\.\w{1,5}\b|\b[\w-]+\.(?:json|md|txt|py|ts|yaml|yml)\b/gi;
const FLAG = /\b[a-z]+(?:_[a-z]+)*=(?:true|false|null|\d+|"[^"]*"|'[^']*')/gi;

const scrub = (text: string): string =>
  text
    .replace(TAG, " ")
    .replace(REMINDER, " ")
    .replace(CODE_SPAN, (span) =>
      /[_()=/:]|\.\w{2,4}$/.test(span) ? " " : span.slice(1, -1)
    )
    .replace(TOOL_CALL, " ")
    .replace(FLAG, " ")
    .replace(UUID, " ")
    .replace(KIND_ID, " ")
    .replace(PATH, " ")
    .replace(OPAQUE_ID, " ")
    .replace(SNAKE_NAME, " ")
    .replace(/\(\s*[,;]?\s*\)/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +([,.;:!?])/g, "$1")
    .replace(/([,;:])(?=[,;:.])/g, "");

const hasWords = (line: string): boolean =>
  (line.match(/[A-Za-z]{2,}/g) ?? []).length >= 2;

/** The reasoning with tool plumbing removed, paragraph breaks kept. */
export const cleanReasoning = (text: string): string =>
  text
    .split("\n")
    .map((line) => scrub(line).trim())
    .filter((line, i, lines) => hasWords(line) || (!line && lines[i - 1]))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const SUMMARY_CHARS = 90;

/** The first sentence of the cleaned reasoning, for the folded row. */
export const reasoningSummary = (text: string): string => {
  const clean = cleanReasoning(text)
    .replace(/^[#>*\-\s]+/gm, "")
    .replace(/\*\*|__/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "";
  const sentences = clean.match(/.+?(?:[.!?](?=\s+["“(]?[A-Z])|$)/g) ?? [clean];
  const sentence =
    sentences
      .map((s) => s.trim())
      .find((s) => (s.match(/[A-Za-z]{2,}/g) ?? []).length >= 3) ?? "";
  if (!sentence) return "";
  if (sentence.length <= SUMMARY_CHARS) return sentence;
  const cut = sentence.slice(0, SUMMARY_CHARS);
  return `${cut
    .slice(0, cut.lastIndexOf(" ") > 40 ? cut.lastIndexOf(" ") : SUMMARY_CHARS)
    .trimEnd()}…`;
};
